import React, { useContext, useMemo } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { LocalizeContext, LocalizeContextProps } from 'react-localize-redux';
import ExportTableButton from '../common/ExportTableButton';
import { ExportColumn } from '../../../../utils/csvDownload';
import { NursingStaffActivityData, NursingStaffActivityRow } from '../hooks/useNursingStaffActivity';

interface NursingStaffActivityTableProps {
  data: NursingStaffActivityData;
  /** Both views. Administrations have no encounter, so the figure is the same on each. */
  showAdministrations: boolean;
}

const KEY = 'hospital.analytics.charts.inpatients.nursingStaff';

/**
 * The administration figure belongs to the person, and a person with two services
 * has two rows carrying the same number. Showing it on the first row only keeps
 * the column sum equal to the total.
 */
export function administrationDisplay(
  rows: NursingStaffActivityRow[],
): (number | null)[] {
  const seen = new Set<number>();
  return rows.map((row) => {
    if (seen.has(row.researcherId)) {
      return null;
    }
    seen.add(row.researcherId);
    return row.administrations;
  });
}

const NursingStaffActivityTable: React.FC<NursingStaffActivityTableProps> = ({
  data,
  showAdministrations,
}) => {
  const { translate } = useContext(LocalizeContext as React.Context<LocalizeContextProps>);
  const t = (id: string) => String(translate(`${KEY}.${id}`));
  const shown = administrationDisplay(data.rows);

  const exportRows = useMemo(() => data.rows.map((row, index) => ({
    ...row,
    administrationsOnce: shown[index],
  })), [data.rows, shown]);

  const columns = useMemo<ExportColumn<typeof exportRows[number]>[]>(() => {
    const base: ExportColumn<typeof exportRows[number]>[] = [
      { header: t('staff'), value: (row) => `${row.surnames} ${row.name}`.trim() },
      { header: t('role'), value: (row) => row.role ?? '' },
      { header: t('service'), value: (row) => row.service ?? '' },
      ...data.surveys.map((survey) => ({
        header: survey.name,
        value: (row: typeof exportRows[number]) => row.counts[survey.id] ?? 0,
      })),
    ];
    if (showAdministrations) {
      base.push({
        header: t('administrations'),
        value: (row) => row.administrationsOnce ?? '',
      });
    }
    return base;
    // t is a fresh function every render; the headers depend on the survey list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.surveys, showAdministrations, translate]);

  const unattributed = data.unattributed;
  const unattributedTotal = unattributed.NONE + unattributed.NOT_APPLICABLE + unattributed.NULL;

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{t('title')}</Typography>
        <ExportTableButton title={t('title')} rows={exportRows} columns={columns} />
      </Box>
      {data.rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('emptyPeriod')}
        </Typography>
      ) : (
        <Table size="small" sx={{ mt: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('staff')}</TableCell>
              <TableCell>{t('role')}</TableCell>
              <TableCell>{t('service')}</TableCell>
              {data.surveys.map((survey) => (
                <TableCell key={survey.id} align="right">{survey.name}</TableCell>
              ))}
              {showAdministrations && <TableCell align="right">{t('administrations')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.rows.map((row, index) => (
              <TableRow key={`${row.researcherId}-${row.service ?? ''}`}>
                <TableCell>{`${row.surnames} ${row.name}`.trim()}</TableCell>
                <TableCell>{row.role ?? '—'}</TableCell>
                <TableCell>{row.service ?? '—'}</TableCell>
                {data.surveys.map((survey) => (
                  <TableCell key={survey.id} align="right">{row.counts[survey.id] ?? 0}</TableCell>
                ))}
                {showAdministrations && (
                  <TableCell align="right">{shown[index] ?? ''}</TableCell>
                )}
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('total')}</TableCell>
              <TableCell />
              <TableCell />
              {data.surveys.map((survey) => (
                <TableCell key={survey.id} align="right" sx={{ fontWeight: 'bold' }}>
                  {data.rows.reduce((sum, row) => sum + (row.counts[survey.id] ?? 0), 0)}
                </TableCell>
              ))}
              {showAdministrations && (
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{data.administrationTotal}</TableCell>
              )}
            </TableRow>
          </TableBody>
        </Table>
      )}
      {unattributed.NULL > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('noBackfill')}
        </Typography>
      )}
      {unattributedTotal > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('unattributed')
            .replace('{none}', String(unattributed.NONE))
            .replace('{notApplicable}', String(unattributed.NOT_APPLICABLE))
            .replace('{unknown}', String(unattributed.NULL))}
        </Typography>
      )}
    </Box>
  );
};

export default NursingStaffActivityTable;
